/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {AfterViewInit, Component, EventEmitter, Input, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {SegmentedButtonOptionModel} from '../../segemented-button/segmented-button-option.model';
import {EntityTreeDisplayModeEnum} from '../../../settings/business-settings/models/entity-tree-display-mode.enum';
import {EntityTreeBranchTypeEnum} from '../models/entity-tree-branch-type.enum';
import {EntityTreeModel} from '../models/entity-tree.model';
import {TreeNodeModel} from '../../../settings/business-settings/models/tree-node-model';
import {EntityPathStateService} from '../services/paths-state/entity-path-state.service';
import {EntityTreeCssMarkersType} from '../entity-tree.css.markers.type';
import {BusinessSettingsBranchDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/business-settings-branch-display-data.model';
import {EntityTreeUtilService} from '../services/entity-tree-util.service';
import {SegmentedButtonDisplayModeEnum} from '../../segemented-button/segmented-button-display-mode.enum';
import {EntityTreeTabPathStateModel} from '../entity-element/models/entity-tree-tab-path-state.model';
import {BusinessSettingsDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/business-settings-display-data.model';

@Component({
    selector: 'app-entity-tree-tabs',
    templateUrl: './entity-tree-tabs.component.html',
    styleUrls: ['./entity-tree-tabs.component.css', '../entity-tree.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EntityTreeTabsComponent implements AfterViewInit {
    @Input() options: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>[] = [];
    @Input() displayMode: EntityTreeDisplayModeEnum;
    @Input() entityTree: EntityTreeModel = {} as EntityTreeModel;
    @Input() entityTreeData: BusinessSettingsDisplayDataModel;
    @Input() branchesData: Map<EntityTreeBranchTypeEnum, BusinessSettingsBranchDisplayDataModel> =
            new Map<EntityTreeBranchTypeEnum, BusinessSettingsBranchDisplayDataModel>();
    @Input() configurationTreeNode: TreeNodeModel = {} as TreeNodeModel;
    @Input() hasAddAndCutSpecificBranch: boolean;

    @Output() selectedOption = new EventEmitter<EntityTreeBranchTypeEnum>();
    @Output() cutTabOption = new EventEmitter<EntityTreeBranchTypeEnum>();
    @Output() addTabOption = new EventEmitter<EntityTreeBranchTypeEnum>();


    @ViewChild('specificBranchButtonTemplate') specificBranchButtonTemplate: TemplateRef<any>;

    public readonly markerType = EntityTreeCssMarkersType;

    get isEditMode(): boolean {
        return this.displayMode === EntityTreeDisplayModeEnum.EDIT;
    }

    get updatedOptions(): SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>[] {
        const currentTabState = this.tabsState();

        this.options.forEach((option) => {
            if (currentTabState != null) {

                currentTabState.get(option.value).unfinishedPath.isOrigin && currentTabState.get(option.value).isActive ?
                        option.inlineStyle = {'border-color': '#d20000'}
                        : option.inlineStyle = null;

                currentTabState.get(option.value).isActive ?
                        option.displayMode = this.getBranchTabsDisplayMode()
                        : option.displayMode = SegmentedButtonDisplayModeEnum.BASE;
            } else {
                option.displayMode = SegmentedButtonDisplayModeEnum.BASE;
                option.inlineStyle = null;
            }
        });
        return this.options;
    }

    get specificBranchTabsClass(): string {
        let baseClass = `specific-branch-tabs ${this.displayMode} margin`;

        if (this.hasAddAndCutSpecificBranch) {
            baseClass = `specific-branch-tabs ${this.displayMode}`;

            if (!this.hasBottomConnectors()) {
                baseClass = `specific-branch-tabs ${this.displayMode} bottom-margin`;
            }
        }

        return baseClass;
    }

    get unfinishedPathMarkerContainerClass(): string {
        return `unfinished-path-marker-container ${this.displayMode}`;
    }

    constructor(private entityPathState: EntityPathStateService, private entityUtil: EntityTreeUtilService) {
    }

    hasTopConnector(option: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): boolean {
        let hasConnector = false;

        const specificBranch = this.entityTree.specificBranches.get(option.value);
        const rootEntity = this.entityUtil.firstValueInBranch(specificBranch,
                this.entityTreeData.entitiesOrder.specificBranches.get(option.value));
        const parentEntity = this.entityUtil.getActiveParentEntity(rootEntity, this.entityTree, this.entityTreeData.entitiesData);

        if (parentEntity != null) {
            hasConnector = this.entityUtil.hasNotNullPath(parentEntity, this.entityUtil.getEntityDisplayData(parentEntity.name,
                    this.entityTreeData.entitiesData));
        }
        return hasConnector;
    }

    tabsState(): Map<string, EntityTreeTabPathStateModel> {
        let currentTreeNode = null;
        let tabsState = null;

        const lastCommonBranchEntity = this.entityUtil.lastValueInBranch(this.entityTree.commonBranch,
                this.entityTreeData.entitiesOrder.commonBranch);

        if (lastCommonBranchEntity != null) {
            currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                    lastCommonBranchEntity, this.configurationTreeNode, this.entityTree, this.entityTreeData.entitiesData);
        }

        if (currentTreeNode != null && currentTreeNode.relationTree != null && lastCommonBranchEntity.path != null) {
            tabsState = currentTreeNode.relationTree.find((entry) => entry.code === lastCommonBranchEntity.path.code).pathState.tabsState;
        }
        return tabsState;
    }

    ngAfterViewInit(): void {
        if (this.options != null) {
            this.options.forEach((option) => option.icon = this.specificBranchButtonTemplate);
        }
    }

    clickOnTab(option: EntityTreeBranchTypeEnum): void {
        this.selectedOption.emit(option);
    }

    bottomLineStyle(): { [key: string]: any } {
        let width = `132px`;
        let marginLeft = 0;

        const selectedTabIndex = this.options.findIndex((item) => item.isSelected);
        const buttonWidth = 76;
        // This value represents the difference between the position of the line that connects to the specific branch
        // and the centre of the tab button
        const correctionForPathWidth = 20;

        if (selectedTabIndex === 1) {
            width = `56px`;
            marginLeft = selectedTabIndex * buttonWidth;
        }

        if (selectedTabIndex >= 2) {
            const correctedIndex = Math.abs(2 - selectedTabIndex);
            width = `calc(21px + calc(${buttonWidth}px * ${correctedIndex}))`;
            marginLeft = (selectedTabIndex * buttonWidth) - correctionForPathWidth;
        }

        return {
            'width': width,
            'margin-left': `${marginLeft}px`
        };
    }

    hasAddButton(tab: EntityTreeBranchTypeEnum): boolean {
        let hasAddButton = true;
        const currentTabsState = this.tabsState();

        if (currentTabsState != null) {
            hasAddButton = !currentTabsState.get(tab).isActive;
        }
        return hasAddButton;
    }

    hasCutButton(tab: EntityTreeBranchTypeEnum): boolean {
        let hasCutButton = false;
        const currentTabsState = this.tabsState();

        if (currentTabsState != null) {
            hasCutButton = currentTabsState.get(tab).isActive;
        }
        return hasCutButton;
    }

    cutButtonClass(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): string {
        let finalClass = 'add-cut-button iwp-icon-gen_cut';
        if (this.specificTabIsUnfinishedPathOrigin(tab)) {
            finalClass += ' adjust-position-with-marker';
        }
        return finalClass;
    }

    clickCutTabOption(option: EntityTreeBranchTypeEnum): void {
        this.cutTabOption.emit(option);
    }

    clickAddTabOption(option: EntityTreeBranchTypeEnum): void {
        this.addTabOption.emit(option);
    }

    hasBottomConnectors(): boolean {
        let hasConnectors = false;

        const selectedTab = this.options.find((option) => option.isSelected);
        const currentTabState = this.tabsState();
        if (currentTabState != null) {
            hasConnectors = currentTabState.get(selectedTab.value).isActive;
        }

        return hasConnectors;
    }

    unfinishedPathTabMarkerMessage(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): string {
        return `There is (are) unfinished path(s) in this matrix related with the ${tab.label}s entities`;
    }

    specificTabIsInUnfinishedPath(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): boolean {
        const currentTabSTate = this.tabsState();

        return (this.entityPathState.treeTabHasUnfinishedPath(this.configurationTreeNode, this.entityTree,
                                this.entityTreeData.entitiesData, tab.value)
                        || this.specificTabIsUnfinishedPathOrigin(tab))
                && (currentTabSTate != null && currentTabSTate.get(tab.value).isActive);
    }

    specificTabIsUnfinishedPathOrigin(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): boolean {
        let isOrigin = false;

        const currentTabSTate = this.tabsState();
        if (currentTabSTate != null) {
            const pathStateValue = currentTabSTate.get(tab.value);
            if (pathStateValue != null) {
                isOrigin = pathStateValue.unfinishedPath.isOrigin && pathStateValue.isActive;
            }
        }
        return isOrigin;
    }

    getGlobalBottomConnectorLineClass(baseClass: string): string {
        let finalClass = baseClass;

        const selectedTab = this.options.find((option) => option.isSelected);

        const selectedTabIsInUnfinishedPath = this.specificTabIsUnfinishedPathOrigin(selectedTab);
        const selectedTabIsInInactivePath = this.specificTabIsInInactivePath(selectedTab);

        if (selectedTabIsInUnfinishedPath) {
            finalClass += ' unfinished-path-line';
        } else if (selectedTabIsInInactivePath) {
            finalClass += ' inactive-path-line';
        }

        return finalClass;
    }

    getGlobalTopConnectorLineClass(baseClass: string): string {
        let finalClass = baseClass;
        const hasOneInactivePathTab = this.options.some((item) => this.specificTabIsInInactivePath(item));

        if (hasOneInactivePathTab) {
            finalClass += ' inactive-path-line';
        }

        // FIXME: Hardcoded logic. Will only work for 3 tabs (2 from one father, 1 from another). Must be reviewed.
        const selectedTabIndex = this.options.findIndex((item) => item.isSelected);
        if (selectedTabIndex >= 2) {
            finalClass += ' right';
        } else if (selectedTabIndex >= 0) {
            finalClass += ' left';
        }

        return finalClass;
    }

    getSpecificTabTopLineClass(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): string {
        let finalClass = 'tab-top-connector-line';

        if (this.specificTabIsUnfinishedPathOrigin(tab)) {
            finalClass += ' unfinished-path-line';
        } else if (this.specificTabIsInInactivePath(tab)) {
            finalClass += ' inactive-path-line';
        }

        return finalClass;
    }

    addButtonDataCy(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): string {
        return `entity-tree-tab-add-button-${tab.value}`;
    }

    cutButtonDataCy(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): string {
        return `entity-tree-tab-cut-button-${tab.value}`;
    }

    private getBranchTabsDisplayMode(): SegmentedButtonDisplayModeEnum {
        let displayMode;
        switch (this.displayMode) {
            case EntityTreeDisplayModeEnum.EDIT:
                displayMode = SegmentedButtonDisplayModeEnum.EDIT;
                break;
            case EntityTreeDisplayModeEnum.VIEW:
                displayMode = SegmentedButtonDisplayModeEnum.VIEW;
                break;
            default:
                displayMode = SegmentedButtonDisplayModeEnum.BASE;
        }
        return displayMode;
    }

    private specificTabIsInInactivePath(tab: SegmentedButtonOptionModel<EntityTreeBranchTypeEnum>): boolean {
        return this.entityPathState.treeTabHasInactivePath(
                this.configurationTreeNode, this.entityTree, this.entityTreeData.entitiesData, tab.value);
    }
}
